import { memo, useId, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import InsightsSectionHeading from "./InsightsSectionHeading";
import InsightsChartBarWithTooltip from "./InsightsChartBarWithTooltip";
import {
  insightsMaxCountAmongBuckets,
  insightsNormalizedBarHeightPct,
} from "../utils/insightsChartHelpers";
import { formatInsightsMetricDisplay } from "../utils/insightsSetRowMath";

/**
 * REP RANGE DISTRIBUTION — column chart; bar height ∝ count within the week.
 *
 * @param {object} props
 * @param {Array<{ id: string, count: number, barClass: string }>} props.buckets
 * @param {number} [props.syncRowIndex] — `data-insights-sync-row`; same index on this section in every week column for `useProgramInsightsCrossColumnRowSync` (see InsightsWeekColumn).
 */
function InsightsRepRangeSection({ buckets, syncRowIndex }) {
  const { t } = useTranslation("athlete-portal");

  /** Stable id for this column’s section title (avoids duplicate ids across week columns). */
  const titleId = useId();

  const maxCount = useMemo(
    () => insightsMaxCountAmongBuckets(buckets),
    [buckets]
  );

  return (
    <section
      className="ProgramInsights-section"
      aria-labelledby={titleId}
      data-insights-sync-row={syncRowIndex}
    >
      <InsightsSectionHeading
        titleId={titleId}
        title={t("programs.programInsights.panel.sectionRepRange")}
        infoAriaLabel={t(
          "programs.programInsights.panel.repRangeDistributionInfo"
        )}
      />
      <div
        className="ProgramInsights-repRangeChart"
        aria-label={t("programs.programInsights.panel.repRangeGroupAria")}
      >
        {buckets.map((b) => {
          const heightPct = insightsNormalizedBarHeightPct(b.count, maxCount);
          const rangeLabel = t(
            `programs.programInsights.panel.repRanges.${b.id}`
          );
          const tooltipBody =
            b.count > 0
              ? t("programs.programInsights.panel.chartBarTooltipSets", {
                  label: rangeLabel,
                  count: formatInsightsMetricDisplay(b.count),
                })
              : undefined;
          const tooltipId = `${titleId.replaceAll(":", "")}-rep-${b.id}`;

          return (
            <div key={b.id} className="ProgramInsights-repRangeCol">
              <InsightsChartBarWithTooltip
                count={b.count}
                heightPct={heightPct}
                tooltipId={tooltipId}
                tooltipBody={tooltipBody}
                wrapClassName="ProgramInsights-repRangeBarWrap"
                barClassName={`ProgramInsights-repRangeBar ${b.barClass}`}
              />
              <span className="ProgramInsights-repRangeLabel">{rangeLabel}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

InsightsRepRangeSection.propTypes = {
  buckets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      barClass: PropTypes.string.isRequired,
    })
  ).isRequired,
  syncRowIndex: PropTypes.number,
};

export default memo(InsightsRepRangeSection);
